import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { User } from "lucide-react";

const Checkout = () => {
  const { items, total, clear } = useCart();
  const { 
    user, 
    isAuthenticated, 
    loginWithGoogle, 
    loginWithEmail, 
    signUpWithEmail, 
    forgotPassword, 
    resetPassword 
  } = useAuth();
  
  const nav = useNavigate();
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", address: "", city: "", pincode: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState(null);

  // Custom auth states
  const [authTab, setAuthTab] = useState("login"); // "login" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // Initialize Google Sign-In script hook
  useEffect(() => {
    if (isAuthenticated) return;

    let checkGoogleInterval = setInterval(() => {
      if (window.google) {
        clearInterval(checkGoogleInterval);
        try {
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "1027150821034-mockclientid123.apps.googleusercontent.com";
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: async (response) => {
              try {
                await loginWithGoogle(response.credential);
                toast.success("Welcome. You may now complete your checkout.");
              } catch (err) {
                console.error(err);
                toast.error("Could not authenticate with Google.");
              }
            },
          });

          const btnElem = document.getElementById("checkout-google-btn");
          if (btnElem) {
            window.google.accounts.id.renderButton(btnElem, {
              theme: "outline",
              size: "large",
              width: 320,
              type: "standard",
              shape: "rectangular",
              text: "signin_with",
              logo_alignment: "left",
            });
          }
        } catch (e) {
          console.error("Error initializing Google Identity on checkout:", e);
        }
      }
    }, 100);

    return () => clearInterval(checkGoogleInterval);
  }, [isAuthenticated, loginWithGoogle, authTab]);

  // Autofill user details once authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setForm((prev) => ({
        ...prev,
        customer_name: prev.customer_name || user.name || "",
        email: prev.email || user.email || "",
      }));
    }
  }, [isAuthenticated, user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setAuthSubmitting(true);
    try {
      await loginWithEmail(email, password);
      toast.success("Welcome. You may now complete your checkout.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setAuthSubmitting(true);
    try {
      await signUpWithEmail(name, email, password);
      toast.success("Account created! You may now complete your checkout.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setAuthSubmitting(true);
    try {
      const res = await forgotPassword(email);
      toast.success(res.message || "Verification code sent to email.");
      setForgotStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset code.");
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email || !otp || !newPassword) {
      toast.error("Please enter OTP code and new password");
      return;
    }
    setAuthSubmitting(true);
    try {
      const res = await resetPassword(email, otp, newPassword);
      toast.success(res.message || "Password updated successfully.");
      setAuthTab("login");
      setForgotStep(1);
      setPassword("");
      setOtp("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password. Check the OTP code.");
    } finally {
      setAuthSubmitting(false);
    }
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return toast.error("Please sign in to place your inquiry.");
    if (items.length === 0) return toast.error("Your cart is empty");
    
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image,
        })),
      };
      const { data } = await api.post("/orders", payload);
      setOrderId(data.id);
      clear();
      toast.success("Order received. We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderId) {
    return (
      <div data-testid="order-success" className="px-6 md:px-12 lg:px-20 py-32 text-center">
        <p className="overline mb-6">Order Received</p>
        <h1 className="font-display text-5xl mb-6">Thank you.</h1>
        <p className="max-w-xl mx-auto leading-relaxed mb-4" style={{ color: "var(--ayu-body)" }}>
          Your enquiry has been received. Our team will contact you within 24 hours to confirm
          availability, shipping, and arrange payment.
        </p>
        <p className="text-sm opacity-60 mb-10">Reference: <span data-testid="order-id">{orderId}</span></p>
        <Link to="/" className="btn-primary">Return to home</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-20 text-center">
        <p className="mb-6" style={{ color: "var(--ayu-body)" }}>Your cart is empty.</p>
        <Link to="/shop/medicines" className="btn-primary">Browse Medicines</Link>
      </div>
    );
  }

  // Enforce Sign-In
  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] px-6 md:px-12 lg:px-20 py-24 flex items-center justify-center bg-[var(--ayu-bg)]">
        <div className="max-w-md w-full bg-white border border-[var(--ayu-border)] p-10 relative overflow-hidden grain shadow-sm rounded-sm">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--ayu-primary)]"></div>
          
          <div className="w-12 h-12 bg-[#FAF8F5] text-[var(--ayu-primary)] flex items-center justify-center mx-auto mb-6 border border-[var(--ayu-border)] rounded-full">
            <User size={20} strokeWidth={1.5} />
          </div>

          <div className="text-center mb-8">
            <p className="overline mb-2">Authentication Required</p>
            <h1 className="font-display text-3xl text-[var(--ayu-heading)]">
              {authTab === "login" && "Sign In to Checkout"}
              {authTab === "signup" && "Create an Account"}
              {authTab === "forgot" && "Recover Password"}
            </h1>
          </div>

          {/* Social Sign-In (Google) - Kept for both signup/signin but hidden for forgot password state */}
          {authTab !== "forgot" && (
            <div className="flex flex-col items-center mb-6">
              <div id="checkout-google-btn" data-testid="google-signin-button" className="min-h-[40px] w-full flex justify-center"></div>
              
              <div className="w-full flex items-center justify-between my-4 text-xs uppercase tracking-wider text-[var(--ayu-muted)]">
                <span className="w-[42%] border-t border-[var(--ayu-border)]"></span>
                <span>or</span>
                <span className="w-[42%] border-t border-[var(--ayu-border)]"></span>
              </div>
            </div>
          )}

          {/* Authentication Forms */}
          {authTab === "login" && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter email"
                  required
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] font-medium">Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthTab("forgot");
                      setForgotStep(1);
                    }}
                    className="text-xs text-[var(--ayu-primary)] hover:underline"
                  >
                    Forgot?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={authSubmitting}
                className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
              >
                {authSubmitting ? "Signing In..." : "Sign In"}
              </button>
              <div className="text-center text-xs text-[var(--ayu-muted)] mt-4">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setAuthTab("signup")}
                  className="text-[var(--ayu-primary)] hover:underline font-semibold"
                >
                  Create one
                </button>
              </div>
            </form>
          )}

          {authTab === "signup" && (
            <form onSubmit={handleSignUp} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Enter email"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                  placeholder="Create password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={authSubmitting}
                className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
              >
                {authSubmitting ? "Creating Account..." : "Create Account"}
              </button>
              <div className="text-center text-xs text-[var(--ayu-muted)] mt-4">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setAuthTab("login")}
                  className="text-[var(--ayu-primary)] hover:underline font-semibold"
                >
                  Sign In
                </button>
              </div>
            </form>
          )}

          {authTab === "forgot" && (
            <div>
              {forgotStep === 1 ? (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <p className="text-xs text-[var(--ayu-muted)] leading-relaxed mb-4 text-center">
                    Enter the email address associated with your sanctuary account. We will send you a 6-digit OTP code to verify your identity.
                  </p>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                      placeholder="Enter email"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={authSubmitting}
                    className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
                  >
                    {authSubmitting ? "Sending Code..." : "Send Verification Code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <p className="text-xs text-[var(--ayu-muted)] leading-relaxed mb-4 text-center">
                    Enter the 6-digit code sent to <strong className="text-[var(--ayu-heading)]">{email}</strong> and create your new secure password.
                  </p>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">6-Digit Verification Code</label>
                    <input
                      type="text"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm text-center font-mono focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                      placeholder="XXXXXX"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[var(--ayu-heading)] mb-1.5 font-medium">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#FAF8F5] border border-[var(--ayu-border)] text-sm focus:outline-none focus:border-[var(--ayu-primary)] transition-colors rounded-sm"
                      placeholder="Enter new password"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={authSubmitting}
                    className="btn-primary w-full justify-center py-3 text-xs uppercase tracking-wider"
                  >
                    {authSubmitting ? "Resetting..." : "Reset Password"}
                  </button>
                </form>
              )}
              <div className="text-center text-xs mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setAuthTab("login");
                    setForgotStep(1);
                  }}
                  className="text-[var(--ayu-muted)] hover:text-[var(--ayu-primary)] font-medium transition-colors"
                >
                  &larr; Back to Login
                </button>
              </div>
            </div>
          )}

          <div className="text-xs opacity-60 flex items-center justify-center gap-1.5 mt-6" style={{ color: "var(--ayu-muted)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--ayu-primary)]"></span>
            Your cart will be preserved
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="px-6 md:px-12 lg:px-20 pt-24 pb-24">
      <p className="overline mb-4">Checkout</p>
      <h1 className="font-display text-5xl mb-12">Place your enquiry</h1>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-8">
          <div>
            <label className="overline block mb-2">Full Name</label>
            <input name="customer_name" data-testid="checkout-name" required value={form.customer_name} onChange={onChange} className="ayu-input" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Email</label>
              <input name="email" type="email" data-testid="checkout-email" required disabled value={form.email} className="ayu-input opacity-70 cursor-not-allowed border-dashed" />
            </div>
            <div>
              <label className="overline block mb-2">Phone</label>
              <input name="phone" data-testid="checkout-phone" required value={form.phone} onChange={onChange} className="ayu-input" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Address</label>
            <input name="address" data-testid="checkout-address" required value={form.address} onChange={onChange} className="ayu-input" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">City</label>
              <input name="city" data-testid="checkout-city" required value={form.city} onChange={onChange} className="ayu-input" />
            </div>
            <div>
              <label className="overline block mb-2">Pincode</label>
              <input name="pincode" data-testid="checkout-pincode" required value={form.pincode} onChange={onChange} className="ayu-input" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Notes (optional)</label>
            <textarea name="notes" data-testid="checkout-notes" rows={4} value={form.notes} onChange={onChange} className="ayu-input" />
          </div>
          <button type="submit" disabled={submitting} data-testid="place-order-button" className="btn-primary w-full justify-center disabled:opacity-60">
            {submitting ? "Placing enquiry…" : "Place Enquiry"}
          </button>
        </div>

        <div className="lg:col-span-5">
          <div className="p-8 sticky top-28" style={{ background: "var(--ayu-surface)" }}>
            <p className="overline mb-6">Your Order ({items.length} item{items.length > 1 ? "s" : ""})</p>
            <ul className="space-y-3 mb-6">
              {items.map((i) => (
                <li key={i.product_id} className="flex justify-between text-sm">
                  <span className="pr-4">{i.name} <span className="opacity-60">× {i.quantity}</span></span>
                  <span>₹{(i.price * i.quantity).toLocaleString("en-IN")}</span>
                </li>
              ))}
            </ul>
            <div className="border-t pt-6 flex justify-between font-display text-xl" style={{ borderColor: "var(--ayu-border)" }}>
              <span>Total</span><span>₹{total.toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
